import React, { useState } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';
const IS_LOCAL = ['localhost', '127.0.0.1'].includes(window.location.hostname);

const Landing = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleDevLogin = async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE}/api/auth/dev-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({}),
      });
      const data = await response.json();
      if (response.ok && data.success) {
        localStorage.setItem('user', JSON.stringify(data.user));
        localStorage.setItem('isAuthenticated', 'true');
        window.location.href = '/dashboard#home';
      } else {
        setError(data.error || 'Dev login failed.');
      }
    } catch (err) {
      setError('Failed to connect to the backend.');
      console.error('Dev login error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE}/api/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'get_auth_url' }),
      });
      const data = await response.json();
      if (data.success) {
        window.location.href = data.auth_url;
      } else {
        setError(data.error || 'Failed to get authentication URL.');
      }
    } catch (err) {
      setError('Failed to connect to authentication service.');
      console.error('Login error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="landing-split" style={{
      display: 'flex',
      minHeight: '100vh',
      fontFamily: 'var(--font-body)',
    }}>
      {/* Left — branding panel */}
      <div className="landing-left" style={{
        flex: 1,
        background: '#EFECE0',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        padding: '48px',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* SVG filter for thickening strokes */}
        <svg width="0" height="0" style={{ position: 'absolute' }}>
          <defs>
            <filter id="thicken">
              <feMorphology operator="dilate" radius="0.1" />
            </filter>
          </defs>
        </svg>

        {/* Logo */}
        <div style={{ position: 'relative', zIndex: 1 }}>
          <img src="/logo.svg" alt="Symbolica" style={{ height: 22 }} />
        </div>

        {/* Full bleed background asset */}
        <img
          src="/login-bg.svg"
          alt=""
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: 0.4,
            pointerEvents: 'none',
            filter: 'url(#thicken)',
          }}
        />

        {/* Spacer */}
        <div style={{ flex: 1 }} />

        {/* Spacer bottom */}
        <div />
      </div>

      {/* Right — login form */}
      <div className="landing-right" style={{
        width: '45%',
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '48px',
        background: 'var(--symbolica-white)',
      }}>
        <div className="landing-form" style={{ width: '100%', maxWidth: 320, position: 'relative', zIndex: 1 }}>
          {/* Mobile logo — hidden on desktop */}
          <img
            src="/logo.svg"
            alt="Symbolica"
            className="landing-mobile-logo"
            style={{ display: 'none', height: 20, marginBottom: 40 }}
          />

          {/* Title */}
          <h1 style={{
            fontFamily: 'var(--font-display)',
            fontSize: '2rem',
            fontWeight: 400,
            color: 'var(--symbolica-black)',
            margin: '0 0 8px',
          }}
          className="landing-title">
            The Landing
          </h1>
          <p style={{
            color: 'var(--symbolica-muted)',
            fontSize: 14,
            margin: '0 0 40px',
            lineHeight: 1.5,
          }}>
            Sign in to access your Symbolica workspace.
          </p>

          {/* Error */}
          {error && (
            <div style={{
              background: '#FEF2F2',
              color: '#B91C1C',
              padding: '12px 16px',
              borderRadius: 'var(--radius-sm)',
              fontSize: 13,
              marginBottom: 20,
              border: '1px solid #E5D5D5',
            }}>
              {error}
            </div>
          )}

          {/* Google sign in */}
          <button
            onClick={handleGoogleLogin}
            disabled={isLoading}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 10,
              padding: '12px 20px',
              background: '#EFECE0',
              color: 'var(--symbolica-black)',
              border: 'none',
              borderRadius: 'var(--radius-sm)',
              fontSize: 14,
              fontWeight: 500,
              fontFamily: 'var(--font-body)',
              cursor: isLoading ? 'default' : 'pointer',
              opacity: isLoading ? 0.6 : 1,
              transition: 'opacity 0.15s',
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            {isLoading ? 'Connecting...' : 'Sign in with Google'}
          </button>

          {/* Divider */}
          {IS_LOCAL && (
            <>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                margin: '24px 0',
              }}>
                <div style={{ flex: 1, height: 1, background: 'var(--symbolica-warm-border)' }} />
                <span style={{ fontSize: 11, color: 'var(--symbolica-muted)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>or</span>
                <div style={{ flex: 1, height: 1, background: 'var(--symbolica-warm-border)' }} />
              </div>

              <button
                onClick={handleDevLogin}
                disabled={isLoading}
                style={{
                  width: '100%',
                  padding: '12px 20px',
                  background: 'transparent',
                  color: 'var(--symbolica-black)',
                  border: '1px solid var(--symbolica-warm-border)',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: 14,
                  fontWeight: 500,
                  fontFamily: 'var(--font-body)',
                  cursor: isLoading ? 'default' : 'pointer',
                  opacity: isLoading ? 0.6 : 1,
                  transition: 'all 0.15s',
                }}
              >
                {isLoading ? 'Connecting...' : 'Dev sign-in (local only)'}
              </button>
            </>
          )}

          {/* Footer note */}
          <p style={{
            marginTop: 40,
            fontSize: 12,
            color: '#B0AFA8',
            lineHeight: 1.5,
          }}>
            Access is restricted to <strong style={{ color: 'var(--symbolica-muted)' }}>@symbolica.ai</strong> accounts.
          </p>
        </div>
      </div>

      {/* Mobile: override to single column */}
      <style>{`
        @media (max-width: 768px) {
          .landing-split { flex-direction: column !important; }
          .landing-split > .landing-left { display: none !important; }
          .landing-split > .landing-right {
            width: 100% !important;
            min-height: 100vh;
            padding: 48px 28px !important;
            justify-content: flex-start !important;
          }
          .landing-split > .landing-right > .landing-form {
            max-width: 100% !important;
            width: 100% !important;
          }
          .landing-mobile-logo {
            display: block !important;
          }
          .landing-split .landing-title {
            font-size: 1.6rem !important;
          }
        }
      `}</style>
    </div>
  );
};

export default Landing;
